// Shared date formatters for self-service surfaces. The DB stores both
// date-only strings (campaign.startDate / endDate as YYYY-MM-DD) and full
// ISO timestamps (created_at, reviewed_at, etc.); each helper handles one.

// Accept either 'YYYY-MM-DD' or a full ISO timestamp (the API may send a
// `date` column back as `2026-05-08T00:00:00.000Z`). Both reduce to the same
// local-day rendering — these formatters never show time.
const toLocalDay = (s: string): Date => new Date(s.slice(0, 10) + 'T00:00:00');

// "Apr 26" — short, used inside progress bar labels. Pass `withYear` to append
// the year ("Apr 26, 2026") — used when a chart spans more than one calendar
// year so labels like "Apr 26" aren't ambiguous across years.
export const formatShortDate = (s?: string | null, withYear = false): string => {
  if (!s) return '—';
  const d = toLocalDay(s);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', ...(withYear ? { year: 'numeric' } : {}),
  });
};

// "April 26, 2026" — long form for body copy / detail rows.
export const formatDate = (dateStr: string | undefined | null): string => {
  if (!dateStr) return '—';
  const d = toLocalDay(dateStr);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
  });
};

// "Jun 17 – Sep 22, 2026" (same year) or "Dec 17, 2026 – Jan 22, 2027"
// (cross-year). Compact range used in the Stat row where the long
// `formatDate` form ("June 17, 2026 – September 22, 2026") overflows the
// 4-column grid and gets ellipsised. Falls back to "—" when either side is
// missing or malformed so the Stat cell still has a placeholder.
export const formatShortRange = (
  start: string | undefined | null,
  end: string | undefined | null,
): string => {
  if (!start || !end) return '—';
  const s = toLocalDay(start);
  const e = toLocalDay(end);
  if (isNaN(s.getTime()) || isNaN(e.getTime())) return '—';
  if (s.getFullYear() === e.getFullYear()) {
    return `${formatShortDate(start)} – ${formatShortDate(end, true)}`;
  }
  return `${formatShortDate(start, true)} – ${formatShortDate(end, true)}`;
};

// "April 26, 2026 at 3:42 PM" — for ISO timestamps that have time-of-day.
export const formatDateTime = (iso: string | undefined | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
};

// `Date.prototype.toISOString()` returns UTC, which can render the wrong
// day when the user is in a non-UTC timezone (anything west of UTC near
// midnight, anything east of UTC during the day). Use this when the
// timestamp should reflect the user's wall clock — e.g., a client-generated
// `created_at` fallback before the server's authoritative value arrives.
//
// Returns "YYYY-MM-DDTHH:MM:SS" (no `Z`) so downstream date-only formatters
// that slice the first 10 chars see the LOCAL date, not the UTC date.
export const toLocalIsoString = (d: Date = new Date()): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
};
